import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { SharingContainerApi } from '@core/sharing-container-types';
import { DateComponent } from '@core/time-ui';
import {
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DividerComponent,
  EmptyStateComponent,
  GroupComponent,
  HeadingComponent,
  SpinnerComponent,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-candidate-sharing-assignments',
  templateUrl: './candidate-sharing-assignments.component.html',
  styleUrls: ['./candidate-sharing-assignments.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    BoxComponent,
    SpinnerComponent,
    GroupComponent,
    CheckboxComponent,
    HeadingComponent,
    DateComponent,
    DividerComponent,
    EmptyStateComponent,
    DialogFooterComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    TranslatePipe,
  ],
})
export class CandidateSharingAssignmentsComponent {
  private readonly i18n = inject(I18nService);
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly isLoading = input<boolean>(false);

  public readonly heading = input<string>('');
  public readonly subheading = input<string>('');
  public readonly assignments = input<number[]>([]);
  public readonly containers = input<SharingContainerApi[]>([]);
  public readonly isLoaded = input<boolean>(false);
  public readonly submitText = input<string>();
  public readonly disableSubmitIfNoAssignments = input<boolean | undefined>(false);
  public cancelClick = output<void>();
  public submitClick = output<number[]>();
  public searchControl = new FormControl<string>('', { nonNullable: true });
  public newAssignments: number[] = [];

  public ngOnChanges(changes: SimpleChanges): void {
    if ('assignments' in changes && !this.isLoading()) {
      this.newAssignments = [...this.assignments()];
    }
  }

  public toggleAssignment(container: SharingContainerApi): void {
    if (this.newAssignments.includes(container.id)) {
      this.newAssignments = this.newAssignments.filter(id => id !== container.id);
    } else {
      const containerId = container.id;
      this.newAssignments.push(containerId);
    }
    this.cdRef.detectChanges();
  }

  public containerName(container: SharingContainerApi): string {
    if (container.name == null) {
      const translationKey = 'app.containers.sharing_container_default_name_atc';
      return this.i18n.translate(translationKey, {
        candidatesCount: container.candidatesCount,
      });
    } else {
      return container.name;
    }
  }

  public isSelected(container: SharingContainerApi): boolean {
    return container && this.newAssignments.includes(container.id);
  }

  public isSearching(): boolean {
    return !!this.searchControl.value && this.searchControl.value.length > 0;
  }

  public cancelSearch(): void {
    this.searchControl.reset('');
  }

  public cancel(): void {
    this.cancelClick.emit();
  }

  public submit(): void {
    this.submitClick.emit(this.newAssignments);
  }

  public trackByContainer(index: number, container: SharingContainerApi): number {
    return container.id;
  }
}
